import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Space, Button, Tag } from 'antd';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ArrowUpOutlined, ArrowDownOutlined, UserOutlined, DollarOutlined } from '@ant-design/icons';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLawyers: 0,
    totalConsultations: 0,
    monthlyGMV: 0,
    platformCommission: 0,
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch stats
        const usersSnap = await getDocs(collection(db, 'users'));
        const lawyersSnap = await getDocs(
          query(
            collection(db, 'lawyers'),
            where('verificationStatus', '==', 'verified')
          )
        );
        const consultationsSnap = await getDocs(
          query(
            collection(db, 'consultations'),
            where('status', '==', 'ended')
          )
        );

        // Calculate metrics
        let totalGMV = 0;
        const consultations = consultationsSnap.docs.map(doc => {
          const data = doc.data();
          totalGMV += data.totalCharged || 0;
          return data;
        });

        setStats({
          totalUsers: usersSnap.size,
          totalLawyers: lawyersSnap.size,
          totalConsultations: consultationsSnap.size,
          monthlyGMV: totalGMV,
          platformCommission: Math.round(totalGMV * 0.25), // 25% commission
        });

        // Fetch recent transactions
        const txnSnap = await getDocs(
          query(
            collection(db, 'walletTransactions'),
            orderBy('createdAt', 'desc'),
            limit(10)
          )
        );

        setRecentTransactions(
          txnSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (text) => <span>{text.substring(0, 8)}...</span>,
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      render: (text) => <span>{text.substring(0, 8)}...</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        let color = type === 'topup' ? 'green' : 'red';
        return <Tag color={color}>{type.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `₹${(amount / 100).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'success' ? 'green' : 'orange'}>{status}</Tag>
      ),
    },
  ];

  return (
    <div>
      <h1>Dashboard</h1>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Verified Lawyers"
              value={stats.totalLawyers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Consultations"
              value={stats.totalConsultations}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Monthly GMV"
              value={`₹${(stats.monthlyGMV / 100).toFixed(0)}`}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Revenue Trend (7 days)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={[]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Consultation Breakdown">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Card title="Recent Transactions" loading={loading}>
        <Table
          columns={columns}
          dataSource={recentTransactions}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;
